/**
 * RegistrationMultiStepTracker Component
 *
 * Displays a multi-step tracker showing the user's IP registration progress across different levels.
 * Renders both a desktop multi-step tracker component and a mobile-friendly step label display.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.registrationStatus - Object mapping registration level names to their status
 *                                           Status values: 'active' or other (inactive)
 *                                           Example: { level1: 'active', level2: 'pending', level3: 'inactive' }
 *
 * @returns {React.ReactElement} JSX element containing the multi-step tracker and informational text
 *
 * @example
 * const registrationStatus = {
 *   'Level 1': 'active',
 *   'Level 2': 'active',
 *   'Level 3': 'pending'
 * }
 * <RegistrationMultiStepTracker registrationStatus={registrationStatus} />
 *
 * Features:
 * - Separates active and inactive registration statuses
 * - Displays active steps first, followed by inactive steps
 * - Shows current step as noOfActiveSteps + 1
 * - Mobile-optimized step labels with aria-hidden for accessibility
 * - Contextual text describing registration completion (1, 2, or 3 levels registered)
 */

import { useMemo } from 'react'
import { MultiStepTracker, Step } from '@americanexpress/dls-react'
import styles from './scss/RegistrationMultiStepTracker.module.scss'

export default function RegistrationMultiStepTracker({ registrationStatus }) {
  const [steps, noOfActiveSteps] = useMemo(() => {
    const activeStatuses = {}
    const nonActiveStatuses = {}

    Object.entries(registrationStatus).forEach(([key, value]) => {
      if (value === 'active') {
        activeStatuses[key] = value
      } else {
        nonActiveStatuses[key] = value
      }
    })

    const statusObj = { ...activeStatuses, ...nonActiveStatuses }

    return [Object.keys(statusObj), Object.keys(activeStatuses).length]
  }, [registrationStatus])

  return (
    <>
      <MultiStepTracker
        className={styles.registrationMultiStepTracker}
        currentStep={noOfActiveSteps + 1}
        variant='default'
      >
        {steps.map(step => (
          <Step key={step} label={step} />
        ))}
      </MultiStepTracker>

      <div className={styles.mobileStepLabels} aria-hidden>
        {steps.map((step, index) => (
          <span
            key={step}
            className={
              index < noOfActiveSteps ? styles.mobileStepLabelActive : styles.mobileStepLabel
            }
          >
            {step}
          </span>
        ))}
      </div>

      <p>
        {noOfActiveSteps === 1 && `Your IP is registered in ${steps[0]} level.`}
        {noOfActiveSteps === 2 && `Your IP is registered in ${steps[0]} and ${steps[1]} levels.`}
        {noOfActiveSteps === 3 && `Your IP registration is complete and active for 24 hours.`}
      </p>
    </>
  )
}
